import { createHash, randomUUID } from "crypto";
import { sql } from "drizzle-orm";
import { releaseInviteCode, reserveInviteCode } from "./invite-code.js";

type ExecuteDb =
  | {
      execute: (query: unknown) => Promise<unknown>;
    }
  | null
  | undefined;

type ReservationRow = {
  invite_code?: string;
};

export type OAuthInviteReservation =
  | { ok: true; token: string; code: string; expiresAt: Date }
  | { ok: false; error: string };

export type OAuthInviteFinalizeResult = { ok: true; code: string } | { ok: false; error: string };

// better-auth 1.6.x expires OAuth state after 10 minutes. Keep invite
// reservations longer so a valid provider callback cannot outlive its slot.
export const BETTER_AUTH_OAUTH_STATE_TTL_MS = 10 * 60 * 1000;
export const OAUTH_INVITE_RESERVATION_TTL_MS = BETTER_AUTH_OAUTH_STATE_TTL_MS + 5 * 60 * 1000;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function hashReservationToken(token: string): string {
  return createHash("sha256").update(token).digest("hex");
}

function normalizeProvider(provider: unknown): string {
  return typeof provider === "string" && provider.trim() ? provider.trim().toLowerCase() : "unknown";
}

export async function releaseExpiredOAuthInviteReservations(db: ExecuteDb): Promise<void> {
  if (!db) return;
  await db.execute(sql`
    WITH released AS (
      UPDATE _gencow_oauth_invite_reservations
      SET status = 'released',
          released_at = NOW()
      WHERE status = 'pending'
        AND expires_at <= NOW()
      RETURNING invite_code
    ),
    release_counts AS (
      SELECT UPPER(invite_code) AS code, COUNT(*)::int AS count
      FROM released
      GROUP BY UPPER(invite_code)
    )
    UPDATE invite_codes
    SET used_count = GREATEST(used_count - release_counts.count, 0)
    FROM release_counts
    WHERE UPPER(invite_codes.code) = release_counts.code
  `);
}

export async function createOAuthInviteReservation(
  db: ExecuteDb,
  input: { inviteCode: unknown; provider: unknown },
): Promise<OAuthInviteReservation> {
  if (!db) return { ok: false, error: "초대 코드 확인이 불가능합니다." };

  await releaseExpiredOAuthInviteReservations(db);
  const reserved = await reserveInviteCode(db, input.inviteCode);
  if (!reserved.ok) return reserved;

  const token = randomUUID();
  const tokenHash = hashReservationToken(token);
  const expiresAt = new Date(Date.now() + OAUTH_INVITE_RESERVATION_TTL_MS);

  try {
    await db.execute(sql`
      INSERT INTO _gencow_oauth_invite_reservations (
        token_hash,
        invite_code,
        provider_id,
        expires_at
      )
      VALUES (
        ${tokenHash},
        ${reserved.code},
        ${normalizeProvider(input.provider)},
        ${expiresAt}
      )
    `);
    return { ok: true, token, code: reserved.code, expiresAt };
  } catch (error) {
    await releaseInviteCode(db, reserved.code);
    console.error("[auth] OAuth invite reservation error:", error);
    return { ok: false, error: "초대 코드 확인이 불가능합니다." };
  }
}

export async function releaseOAuthInviteReservation(db: ExecuteDb, token: unknown): Promise<void> {
  if (!db || typeof token !== "string" || !token.trim()) return;
  await db.execute(sql`
    WITH released AS (
      UPDATE _gencow_oauth_invite_reservations
      SET status = 'released',
          released_at = NOW()
      WHERE token_hash = ${hashReservationToken(token)}
        AND status = 'pending'
      RETURNING invite_code
    ),
    release_counts AS (
      SELECT UPPER(invite_code) AS code, COUNT(*)::int AS count
      FROM released
      GROUP BY UPPER(invite_code)
    )
    UPDATE invite_codes
    SET used_count = GREATEST(used_count - release_counts.count, 0)
    FROM release_counts
    WHERE UPPER(invite_codes.code) = release_counts.code
  `);
}

export async function finalizeOAuthInviteReservation(
  db: ExecuteDb,
  token: unknown,
  user: { id: string; email: string },
): Promise<OAuthInviteFinalizeResult> {
  if (!db || typeof token !== "string" || !token.trim()) {
    return { ok: false, error: "Invalid invite reservation" };
  }

  await releaseExpiredOAuthInviteReservations(db);
  const finalized = await db.execute(sql`
    UPDATE _gencow_oauth_invite_reservations
    SET status = 'completed',
        user_id = ${user.id},
        user_email = ${user.email},
        completed_at = NOW()
    WHERE token_hash = ${hashReservationToken(token)}
      AND status = 'pending'
      AND expires_at > NOW()
    RETURNING invite_code
  `);
  const row = rowsFromResult<ReservationRow>(finalized)[0];
  if (!row?.invite_code) return { ok: false, error: "Invalid invite reservation" };
  return { ok: true, code: String(row.invite_code).toUpperCase() };
}
